import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import EventsService from "../../services/EventsService";
import Consts from "../../commons/Consts";
import moment from "moment";
import { Button } from "flowbite-react";
import { IoLocation, IoTicketOutline, IoTime, IoTrashOutline } from "react-icons/io5";
import EventTicketPageItem from "../../components/EventTicketPageItem";
import Helpers from "../../utils/Helpers";
import { t } from "i18next";
import { PaymentInstance } from "../../components/PaymentInstance";
import TicketsBuyingCheckingModal from "../../components/TicketsBuyingCheckingModal";

const EventDetails = () => {
    const params = useParams();
    const [event, setEvent] = useState(null);
    const [startsAt, setStartsAt] = useState(moment(new Date()));
    const [endsAt, setEndsAt] = useState(moment(new Date()));
    const [loading, setLoading] = useState(null);
    const [cart, setCart] = useState({});
    const [payInstanceOpened, setPayInstanceOpened] = useState(false);
    const [showOrderCheckingModal, setShowOrderCheckingModal] = useState(false);

    const paymentToken = (new URLSearchParams(window.location.search)).get('token');
    // https://3901.tunnels.chooz-app.com/events/1?token=gbZFfJ9PeiN86mm8wI9Q

    const getEvent = () => {
        setLoading('get-envent');

        EventsService.get(params.event_id)
            .then(response => {
                setEvent(response.events);
                setStartsAt(moment(response.events.starts_at));
                setEndsAt(moment(response.events.ends_at));
            })
            .catch(err => console.log(err))
            .finally(() => setLoading(null));
    };

    const updateCart = (ticket, toadd) => {
        setCart(state => ({
            ...state,
            [ticket.id]: state[ticket.id]
                ? { ...state[ticket.id], qty: state[ticket.id].qty + toadd }
                : { id: ticket.id, type: ticket.type, price: ticket.price, qty: 1 }
        }));
    };

    const getCartContent = () => {
        const arr = Object.values(cart);
        return arr.filter(x => !!x.qty);
    };

    const getCartAmount = () => {
        return getCartContent().reduce((prev, next) => prev + (next.price * next.qty), 0);
    };

    const emptyCart = () => {
        Helpers.alert.confirm('Êtes vous sur de vouloir continuer ?')
            .then(() => setCart({}))
            .catch(() => null);
    };

    useEffect(() => {
        getEvent();
        paymentToken && setShowOrderCheckingModal(true);
    }, []);

    return (
        <>
        <div className={`bg-[#F1F1F1] pb-10`}>
            <section className={`bg-gray-900`}>
                <div className={`max-h-[500px] flex items-center justify-center overflow-hidden`}>
                    <img
                        src={`${Consts.basefile}/events/covers/${event?.cover}`}
                        className={`w-full`}
                    />
                </div>
            </section>
            <section className={`bg-gray-900`}>
                <div className={`max-w-screen-2xl mx-auto px-7 lg:px-20 py-8 flex gap-9 items-center`}>
                    <div className={`text-center shrink-0`}>
                        <span className={`text-[29px] leading-3 text-neutral-300 capitalize`}>{startsAt.format('ddd').replace('.', '')}</span>
                        <h1 className={`text-orange-500 font-bold text-5xl`}>{startsAt.format('DD')}</h1>
                        <span className={`block p-1 text-xl bg-orange-500 text-white mt-0.5 rounded-sm uppercase`}>{startsAt.format('MMM').replace('.', '')}</span>
                    </div>
                    <div className={`h-[100px] border-l border-gray-700`}></div>
                    <div className={``}>
                        <h1 className={`text-xl md:text-3xl text-white font-bold`}>{event?.name}</h1>
                        <div className={`flex items-center mt-2.5 md:mt-3`}>
                            <IoTime className={`text-gray-400 text-lg md:text-2xl`} />
                            <span className={`text-base md:text-lg text-gray-600 ml-2`}>
                                {startsAt.format('HH[h]mm')} - {endsAt.format('HH[h]mm')}
                            </span>
                        </div>
                        <div className={`flex items-center mt-1`}>
                            <IoLocation className={`text-gray-400 text-lg md:text-2xl`} />
                            <span className={`text-base md:text-lg text-gray-600 ml-2`}>{event?.location_name || 'Aucun endroit spécifié.'}</span>
                        </div>
                    </div>
                    <div className={`ml-auto shrink-0 hidden md:block`}>
                        <Button
                            className={`h-14 px-2 lg:px-5 bg-orange-500 hover:!bg-orange-600`}
                        >
                            <IoTicketOutline size={20} className={`lg:mr-3`} />
                            <span className="hidden lg:inline-block">Reserver mes places</span>
                        </Button>
                    </div>
                </div>
            </section>
            <section className={`mt-10`}>
                <div className={`max-w-screen-2xl mx-auto px-5 lg:px-20 md:flex gap-5`}>
                    {/* left */}
                    <div className={`flex-1`}>
                        <div className={`bg-white rounded-md p-6 lg:p-10`}>
                            <h2 className={`text-2xl font-bold text-gray-700 m-0`}>Les tickets</h2>
                            <div className={`mt-5`}>
                                {(event?.tickets || []).map(item => (
                                    <div className={`mb-5`} key={item.id}>
                                        <EventTicketPageItem
                                            item={item}
                                            onChangeQty={toadd => updateCart(item, toadd)}
                                            qty={cart[item.id]?.qty || 0}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={`mt-10 bg-white rounded-md p-6 lg:p-10`}>
                            <h2 className={`text-2xl font-bold text-gray-700 m-0`}>A propos de cet évènement</h2>
                            <div className={`text-base [&_*]:!text-gray-600 mt-5 w-full [&_p]:mb-4`}>
                                {parse(DOMPurify.sanitize((event?.description || '').replace(/&nbsp;/g, ' ')))}
                            </div>
                        </div>
                    </div>
                    {/* right */}
                    <div className={`w-full mt-5 md:mt-0 md:w-72 lg:w-96`}>
                        <div className={`bg-white rounded-md p-5 lg:p-7 md:sticky top-8`}>
                            <h2 className={`text-lg font-bold text-gray-700`}>Ma commande</h2>
                            {!getCartContent().length ? (
                                <div className={`p-5 text-gray-600 bg-neutral-100 rounded-md mt-4`}>
                                    Vous n'avez pas reservé de billet pour cet évènement.
                                </div>
                            ) : (
                                <div className={`mt-5`}>
                                    <ul className={`m-0 p-0 flex flex-col gap-4`}>
                                        {getCartContent().map(line => (
                                            <li className={`flex text-sm text-gray-500`} key={line.id}>
                                                <span className={`w-44`}>{`${line.qty}x ${line.type}`}</span>
                                                <span className={`ml-auto`}>{line.price}F</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className={`mt-5 pt-5 border-t border-gray-100`}>
                                        <div className={`flex text-sm text-gray-500`}>
                                            <span className={``}>Sous-total</span>
                                            <span className={`ml-auto`}>{getCartAmount()}F</span>
                                        </div>
                                        <div className={`flex text-sm text-gray-500 mt-4`}>
                                            <span className={``}>Frais</span>
                                            <span className={`ml-auto`}>{0}F</span>
                                        </div>
                                    </div>
                                    <div className={`mt-5 pt-4 border-t border-gray-100`}>
                                        <div className={`flex text-sm text-gray-500`}>
                                            <span className={`text-lg text-gray-800 font-bold`}>Total</span>
                                            <div className={`ml-auto`}>
                                                <span className={`block text-right text-lg text-gray-800 font-bold`}>{getCartAmount()}F CFA</span>
                                                <span className={`block text-right text-xs text-gray-400`}>Ce prix inclut la taxe</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`mt-8 flex gap-2`}>
                                        <Button
                                            className={`bg-orange-500 hover:!bg-orange-600 h-12 flex-1`}
                                            onClick={() => setPayInstanceOpened(true)}
                                        >COMMANDER MES BILLETS
                                        </Button>
                                        <Button
                                            className={`bg-gray-700 hover:!bg-gray-800 h-12`}
                                            onClick={() => emptyCart()}
                                        >
                                            <IoTrashOutline size={18} />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>                
            </section>
        </div>

        {showOrderCheckingModal ? (
            <TicketsBuyingCheckingModal
                token={paymentToken}
                onDismiss={() => setShowOrderCheckingModal(false)}
            />
        ) : null}

        <PaymentInstance
            opened={payInstanceOpened}
            options={{
                setupURL: `${Consts.baseapi}/events/${params.event_id}/tickets-pay/initialize`,
                setupMethod: 'POST',
                setupData: {
                    cart: getCartContent()
                },
                buttonHTML: t('Payer mes billets') + '.',
                mandatoryInfos: {
                    'wave-pay': ['email', 'fullName'],
                    'others': ['email', 'fullName'],
                }
            }}
            onSuccess={(token, data) => {
                setLoading(null);
            }}
            onError={err => {
                setLoading(null);
                err?.message && Helpers.alert.error(err.message);
            }}
            onClose={value => {
                setPayInstanceOpened(false);
                !value?.is_wave && setLoading(null);
                // value?.is_wave && checkEnrollFromToken(value?.token, 5, 10, false);
            }}
        />
        </>
    );
};

export default EventDetails;